import { ReactComponent as ThumbUp } from "../../../assets/icons/thumb-up.svg";
import { ReactComponent as Close } from "../../../assets/icons/x.svg";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../../components/Button";
import { useSurvey } from "../../../integrations/posthog/useSurvey";
import { twMerge } from "tailwind-merge";
import Lottie from "lottie-react";
import * as animationData from "../../../assets/lotties/check_success.json";

type Feedback = {
  feedback: string[];
  otherFeedback: string;
  type?: number;
};

function FeedbackItem({
  feedback,
  type,
  isSelected,
  onClick,
}: {
  feedback: string;
  type?: number;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer w-[135px] flex flex-col items-center justify-center border rounded-md py-2 ${isSelected ? (type === 1 ? "bg-[#EAFFF1] border-[#189352]" : "bg-[#FFD6D7] border-[#D32626]") : "bg-gray-100 border-transparent"}`}
    >
      <span className="text-[12px]">{feedback}</span>
    </div>
  );
}

export function FeedbackView({ sessionId, showCompactFeedback = false }: { sessionId: string, showCompactFeedback?: boolean }) {
  const [feedback, setFeedback] = useState<Feedback>({
    feedback: [],
    otherFeedback: "",
    type: undefined,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const {
    shouldShowSurvey,
    isLoading: surveyLoading,
    trackSurveyShown,
    trackSurveySent,
    trackSurveyDismissed,
    setShouldShowSurvey,
  } = useSurvey(sessionId);

  const options = useMemo(() => {
    if (feedback.type === 1) {
      return [
        "Clear responses",
        "Compliance-Friendly",
        "Smooth Experience",
        "Engaging and Natural",
        "Other (Please Specify)",
      ];
    } else if (feedback.type === 0) {
      return [
        "Unclear Response ",
        "Compliance Issue",
        "Technical Issue",
        "Didn't Feel Natural",
        "Other (Please Specify)",
      ];
    }
    return [];
  }, [feedback.type]);

  useEffect(() => {
    if (shouldShowSurvey) {
      trackSurveyShown();
    }
  }, [shouldShowSurvey, trackSurveyShown]);

  const handleThumbUp = () => {
    setFeedback((prev) => ({
      ...prev,
      type: 1,
      feedback: [],
      otherFeedback: "",
    }));
  };

  const handleThumbDown = () => {
    setFeedback((prev) => ({
      ...prev,
      type: 0,
      feedback: [],
      otherFeedback: "",
    }));
  };

  const handleFeedback = (option: string) => {
    setFeedback((prev) => {
      const payload = {
        ...prev,
      };
      const newFeedback = [...prev.feedback];
      if (newFeedback.includes(option)) {
        newFeedback.splice(newFeedback.indexOf(option), 1);
        if (option === "Other (Please Specify)") {
          payload.otherFeedback = "";
        }
      } else {
        newFeedback.push(option);
      }
      return {
        ...prev,
        ...payload,
        feedback: newFeedback,
      };
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    // Track survey sent with PostHog
    const thumbType = feedback.type === 1 ? "up" : "down";
    const chips = feedback.feedback
      .map((item) =>
        item === "Other (Please Specify)" ? feedback.otherFeedback || null : item
      )
      .filter(Boolean) as string[];
    await trackSurveySent(thumbType as "up" | "down", chips);

    setIsLoading(false);
    setSuccess(true);
    successTimeoutRef.current = setTimeout(() => {
      setSuccess(false);
      setShouldShowSurvey(false);
    }, 2000);
  };

  const isSelected = (option: string, feedback: string[]) => {
    return feedback.includes(option);
  };

  if (surveyLoading) {
    return null;
  }

  return (
    !shouldShowSurvey ? 
      null : 
    <>
      <div className="flex z-[1] flex-col w-fit max-w-[360px] absolute top-24 right-6 items-center justify-center bg-white px-4 py-2 rounded-[10px] shadow-[0px_3.819px_8.975px_0px_rgba(0,0,0,0.25)]">
        {feedback.type !== undefined && <button
          className="absolute top-2 right-2"
          onClick={() => {
            trackSurveyDismissed();
            setFeedback({
              feedback: [],
              otherFeedback: "",
              type: undefined,
            });
            setShouldShowSurvey(false);
          }}
        >
          <Close className="w-4 h-4 [&_path]:stroke-[#1A1A1A] [&_path]:stroke-2" />
        </button>}
        {success ? (
          <div className="flex w-[310px] items-center justify-between">
            <div className="text-xs font-semibold">
              Feedback submitted! Thanks!
            </div>
            <Lottie
              animationData={animationData}
              loop={true}
              className="w-[clamp(33px,3vw,44px)] h-[clamp(33px,3vw,44px)]"
            />
          </div>
        ) : (
          <>
            <div className={twMerge("flex items-center justify-between gap-8", feedback.type !== undefined && "mt-4")}>
              {!showCompactFeedback || (feedback.type !== undefined) ? <div className="text-xs font-semibold">
              How was the quality of your call?
            </div> : null}
            <div className="flex flex-row items-center justify-center gap-3">
              <button
                disabled={isLoading}
                className={`flex flex-col items-center justify-center w-[clamp(33px,3vw,44px)] h-[clamp(33px,3vw,44px)] bg-gray-100 hover:bg-gray-100/80 rounded-full ${feedback.type === 1 ? "!bg-[#EAFFF1]/70" : "bg-gray-100"}`}
                onClick={handleThumbUp}
              >
                <ThumbUp className="w-[clamp(22px,2vw,28px)] h-[clamp(22px,2vw,28px)]" />
              </button>
              <button
                disabled={isLoading}
                className={`flex flex-col items-center justify-center w-[clamp(33px,3vw,44px)] h-[clamp(33px,3vw,44px)] bg-gray-100 hover:bg-gray-100/80 rounded-full ${feedback.type === 0 ? "!bg-[#FFD6D7]" : "bg-gray-100"}`}
                onClick={handleThumbDown}
              >
                <ThumbUp className="rotate-180 [&_path]:fill-[#E13434] w-[clamp(22px,2vw,28px)] h-[clamp(22px,2vw,28px)]" />
              </button>
            </div>
            </div>
            {feedback.type !== undefined && (
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                {options.map((option, index) => (
                  <FeedbackItem
                    onClick={() => !isLoading && handleFeedback(option)}
                    key={index}
                    feedback={option}
                    type={feedback.type}
                    isSelected={isSelected(option, feedback.feedback)}
                  />
                ))}
              </div>
            )}
            {feedback.type !== undefined && feedback.feedback.includes("Other (Please Specify)") && (
              <div className="mt-4 flex w-full flex-col items-center justify-center">
                <textarea
                  readOnly={isLoading}
                  className="w-full h-[100px] border rounded-md p-2"
                  value={feedback.otherFeedback}
                  onChange={(e) =>
                    setFeedback((prev) => ({ ...prev, otherFeedback: e.target.value }))
                  }
                />
              </div>
            )}
            {feedback.type !== undefined && <Button
              variant="primary"
              loading={isLoading}
              disabled={feedback.type === undefined}
              className="!py-3 mt-4 !w-40 !rounded-full"
              onClick={handleSubmit}
            >
              Submit
            </Button>}
          </>
        )}
      </div>
    </>
  );
}
